import { isEmpty } from "utils";

const GLOBAL_VALUE_TYPES = {
  categoricalValues: "categoricalValues",
  flagValues: "flagValues",
  numericValues: "numericValues",
  dateValues: "dateValues",
  geoLocation: "geoLocation",
};

const OPERATORS = {
  or: "or",
  and: "and",
};

const USER_TYPES_WITH_FILTER_ACCESS = {
  admin: "ADMIN",
  freelancer: "FREELANCER",
  recruiter: "RECRUITER",
  partner: "PARTNER",
  showcase: "SHOWCASE",
  client: "CLIENT",
  signal: "SIGNAL",
};

const FLAG_FILTER_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const GEO_LOCATION_FILTER_DEFAULTS = {
  radius: 50,
  unit: "km",
};

/**
 * Global search filter configuration for the user list.
 * This is used to filter users based on various criteria.
 */
const operatorFunc = (val) => (val ? OPERATORS.and : OPERATORS.or);

/**
 * Checks if a specific filter can be used based on the user roles.
 * @param {Object} configs - The filter configurations.
 * @param {*} filterKey - The key of the filter to check.
 * @param {*} roles - The user roles to check against.
 * @returns {boolean} - True if the filter can be used, false otherwise.
 */
const canUseFilter = (configs, filterKey, roles) => {
  const filter = configs[filterKey];
  const roleNames = roles?.map(({ name }) => name);

  if (!filter || !filter.restrict) return false;

  const { roles: allowedRoles } = filter.restrict;

  return allowedRoles?.some((allowed) => roleNames.includes(allowed));
};

/**
 * Transforms the filters based on the provided configurations.
 * This function processes the filters to match the expected format for the search query.
 * @param {Object} configs - The filter configurations.
 * @param {Object} filters - The current filters applied in the search.
 * @return {Array} - An array of transformed filters ready for the search query.
 */
const getTransformedFilters = (configs, filters) =>
  Object.entries(filters)
    .filter(([, { value, enabled }]) => !isEmpty(value) && !!enabled)
    .reduce((acc, [key, { value, operator }]) => {
      const { valuesType, transformValues } = configs[key] || {};
      const transformedValue = transformValues?.(value) ?? value;

      if (!valuesType || isEmpty(transformedValue)) return acc;

      let entry = { key, [valuesType]: transformedValue };
      if (operator) entry = { ...entry, operator: operatorFunc?.(operator) };

      acc.push(entry);

      return acc;
    }, []);

/**
 * Checks if a specific sort option can be used based on the current applicant state.
 * @param {object} option - The sort option to check.
 * @param {string} currentState - The current applicant state.
 * @returns {boolean} - True if the sort option can be used, false otherwise.
 */
const canUseSortOption = (option, currentState) => {
  if (!option) return false; // Invalid option
  if (!option.restrict) return true; // No restriction = available everywhere

  const { states } = option.restrict;

  return states?.includes(currentState);
};

/**
 * Filters sorting options based on the current applicant state.
 * @param {Array} sortOptions - The sorting options array.
 * @param {string} currentState - The current applicant state.
 * @returns {Array} - Filtered array of sort options.
 */
const getAllowedSortOptions = (sortOptions, currentState) =>
  sortOptions.filter((option) => canUseSortOption(option, currentState));

export {
  GLOBAL_VALUE_TYPES,
  OPERATORS,
  USER_TYPES_WITH_FILTER_ACCESS,
  FLAG_FILTER_OPTIONS,
  GEO_LOCATION_FILTER_DEFAULTS,
  canUseFilter,
  getTransformedFilters,
  getAllowedSortOptions,
};
